// ========================================
// 原始属性カードの固有効果
// ========================================

import {
  getPlayerContext,
  conditionalDamage,
  searchCard,
  reviveFromGraveyard,
  modifyAttack,
  applyStatusToAllOpponentMonsters,
  selectAndApplyStatusToOpponent,
} from '../effectHelpers';
import { hasCategory } from '../../utils/helpers';
import { statusEffectEngine, STATUS_EFFECT_TYPES } from '../statusEffects';
import type { CardEffectRegistry } from './index';
import type { MonsterInstance, CardWithId, CardData, PlayerNumber } from '../../types';

/**
 * 原始属性カードの固有効果
 */
export const primitiveCardEffects: CardEffectRegistry = {
  /**
   * C0000002: 触覚持ち粘液獣
   * 基本技：次の相手エンドフェイズ時まで相手は魔法カードを使えない
   */
  C0000002: (skillText, context) => {
    if (context.skillType === 'basic') {
      // setP1MagicBlocked/setP2MagicBlockedは特殊なステートのためcontext直接参照
      const { addLog, currentPlayer, setP1MagicBlocked, setP2MagicBlocked } = context;

      // 相手の魔法カード使用をブロック
      if (currentPlayer === 1) {
        setP2MagicBlocked(true);
        addLog('触覚持ち粘液獣の効果: プレイヤー2は次のエンドフェイズまで魔法カードを使用できない！', 'info');
      } else {
        setP1MagicBlocked(true);
        addLog('触覚持ち粘液獣の効果: プレイヤー1は次のエンドフェイズまで魔法カードを使用できない！', 'info');
      }

      return true;
    }
    return false;
  },

  /**
   * C0000004: ゴミあさり粘液獣
   * 【召喚時】墓地の《粘液獣》モンスター1体を攻撃力半減して場に戻す
   * ※召喚時効果はトリガーシステムで処理
   */
  // C0000004は基本技がないため、cardEffectsでの実装は不要

  /**
   * C0000009: 粘液獣・寄生
   * 基本技: このカードを相手モンスターに重ねて発動。
   * 対象の攻撃力を毎ターン開始時に500ダウン、次の相手エンドフェイズまで対象モンスターの効果を無効化する。
   * 場に粘液獣が3体以上いる場合、さらに攻撃力を500ダウン。
   */
  C0000009: (skillText, context) => {
    if (context.skillType === 'basic') {
      const { addLog, monsterIndex, setPendingMonsterTarget } = context;
      const { myField, setMyField, opponentField, setOpponentField, currentPlayer } = getPlayerContext(context);

      // このカードの情報を取得
      const thisMonster = myField[monsterIndex];
      if (!thisMonster) {
        addLog('粘液獣・寄生: カードが見つかりません', 'info');
        return false;
      }

      // 相手モンスターがいるかチェック
      const hasOpponentMonster = opponentField.some(m => m !== null);
      if (!hasOpponentMonster) {
        addLog('粘液獣・寄生: 寄生する対象がいません', 'info');
        return false;
      }

      // 場の粘液獣の数をカウント（自分を除く）
      const slimeCount = myField.filter((m, idx) =>
        m && idx !== monsterIndex && hasCategory(m, '【スライム】')
      ).length;

      // ATK減少量を計算
      const atkReduction = slimeCount >= 3 ? 1000 : 500;

      // モンスター選択UIを表示
      setPendingMonsterTarget({
        message: '寄生する相手モンスターを選択してください',
        targetPlayer: 'opponent',
        callback: (targetIndex: number) => {
          const target = opponentField[targetIndex];
          if (!target) return;

          // このカードを自分フィールドから除去（墓地には送らない、寄生として付与）
          setMyField(prev => prev.map((m, idx) => {
            if (idx === monsterIndex) {
              return null;
            }
            return m;
          }));

          // 相手モンスターにPARASITE状態異常を付与
          setOpponentField(prev => prev.map((m, idx) => {
            if (idx === targetIndex && m) {
              // 状態異常を付与（直接mをmutateせず、新しいオブジェクトで返す）
              const updatedMonster = { ...m };
              if (!updatedMonster.statusEffects) {
                updatedMonster.statusEffects = [];
              }
              // 寄生状態異常を追加
              statusEffectEngine.applyStatus(updatedMonster, STATUS_EFFECT_TYPES.PARASITE, {
                value: atkReduction,
                parasiteCard: { ...thisMonster },
                parasiteOwner: currentPlayer,
                effectNegated: true, // 効果無効化フラグ
                source: 'C0000009',
                sourceName: '粘液獣・寄生',
              });
              return updatedMonster;
            }
            return m;
          }));

          addLog(`粘液獣・寄生が${target.name}に寄生した！`, 'info');
          addLog(`毎ターン開始時に攻撃力-${atkReduction}、効果無効化！`, 'info');
        },
        selectionOwner: context.currentPlayer,
      });

      return true;
    }
    return false;
  },

  /**
   * C0000006: 新・超覚醒粘液獣ハイパー
   * 【召喚時】場にいる粘液獣1体につき、自身の攻撃力を1000アップ
   * ※召喚時効果はトリガーシステムで処理
   * 基本技：場にいる時一度だけ相手モンスター1体を飲み込んで無効化する
   */
  C0000006: (skillText, context) => {
    const { addLog, monsterIndex, setPendingMonsterTarget } = context;
    const { myField, setMyField, opponentField, setOpponentField } = getPlayerContext(context);

    // 基本技：相手モンスター1体を飲み込む（1回限り、2ターン後に消化）
    if (context.skillType === 'basic') {
      // このカードの情報を取得
      const thisMonster = myField[monsterIndex];
      if (!thisMonster) {
        addLog('新・超覚醒粘液獣ハイパー: カードが見つかりません', 'info');
        return false;
      }

      // 一度だけ使用可能チェック
      if (thisMonster.hasUsedSwallow) {
        addLog('新・超覚醒粘液獣ハイパー: すでに飲み込み効果を使用済み', 'info');
        return false;
      }

      // 相手モンスターがいるかチェック
      const hasOpponentMonster = opponentField.some(m => m !== null);
      if (!hasOpponentMonster) {
        addLog('新・超覚醒粘液獣ハイパー: 飲み込む対象がいません', 'info');
        return false;
      }

      // モンスター選択UIを表示
      setPendingMonsterTarget({
        message: '飲み込むモンスターを選択してください',
        targetPlayer: 'opponent',
        callback: (targetIndex: number) => {
          const target = opponentField[targetIndex];
          if (!target) return;

          // 相手モンスターをフィールドから除外
          setOpponentField(prev => prev.map((m, idx) => {
            if (idx === targetIndex) {
              return null;
            }
            return m;
          }));

          // 自身に飲み込んだモンスターを保存（2ターン後に消化）
          setMyField(prev => prev.map((m, idx) => {
            if (idx === monsterIndex && m) {
              return {
                ...m,
                hasUsedSwallow: true,
                swallowedMonster: { ...target, originalSlotIndex: targetIndex },
                digestCounter: 2,
              };
            }
            return m;
          }));

          addLog(`新・超覚醒粘液獣ハイパーが${target.name}を飲み込んだ！（消化まで2ターン）`, 'info');
        },
        selectionOwner: context.currentPlayer,
      });

      return true;
    }

    return false;
  },

  /**
   * C0000007: 粘液獣・キング
   * 基本技：このターン中、相手モンスターの効果を全て無効化する。
   */
  C0000007: (skillText, context) => {
    if (context.skillType === 'basic') {
      const { addLog } = context;
      const { opponentField } = getPlayerContext(context);

      // 相手モンスターがいるかチェック
      const hasOpponentMonster = opponentField.some(m => m !== null);
      if (!hasOpponentMonster) {
        addLog('粘液獣・キング: 無効化する対象がいません', 'info');
        return true; // 空振りでも発動成功
      }

      // 相手モンスター全体にSILENCE（効果無効）を付与（ターン終了まで）
      applyStatusToAllOpponentMonsters(context, STATUS_EFFECT_TYPES.SILENCE, {
        duration: 0, // ターン終了時まで
        source: 'C0000007',
        sourceName: '粘液獣・キング',
      }, '粘液獣・キング');

      addLog('粘液獣・キング: このターン中、相手モンスターの効果を全て無効化！', 'info');
      return true;
    }
    return false;
  },

  /**
   * C0000008: 粘液獣・胞子
   * 【召喚時】デッキから《粘液獣》と名の付くカード1枚を手札に加える
   * ※召喚時効果はトリガーシステムで処理
   */
  C0000008: (skillText, context) => {
    // 召喚時効果はトリガーで処理されるため、cardEffectsでは何もしない
    return false;
  },

  /**
   * C0000010: 粘液獣・融合体
   * 【召喚時】このカードと場の他の《粘液獣》1体を融合し、このカードの攻撃力を融合素材の合計に変更する
   * ※召喚時効果はトリガーシステムで処理
   * 基本技：ターン終了時まで相手モンスター1体の効果を無効化する。
   */
  C0000010: (skillText, context) => {
    // 基本技：ターン終了時まで相手モンスター1体の効果を無効化
    if (context.skillType === 'basic') {
      const { addLog } = context;
      const { opponentField } = getPlayerContext(context);

      // 相手モンスターがいるかチェック
      const hasOpponentMonster = opponentField.some(m => m !== null);
      if (!hasOpponentMonster) {
        addLog('粘液獣・融合体: 無効化する対象がいません', 'info');
        return false;
      }

      // 相手モンスター1体を選択してSILENCE（効果無効）を付与
      selectAndApplyStatusToOpponent(context, STATUS_EFFECT_TYPES.SILENCE, {
        duration: 0, // ターン終了時まで
        source: 'C0000010',
        sourceName: '粘液獣・融合体',
      }, '粘液獣・融合体');

      return true;
    }

    // 召喚時効果はトリガーで処理されるため、cardEffectsでは何もしない
    return false;
  },

  /**
   * C0000011: 粘液獣・暴走体
   * 【召喚時】場にいる《粘液獣》1体を破壊し、そのコスト×300ダメージを相手プレイヤーに与える
   * ※召喚時効果はトリガーシステムで処理
   */
  C0000011: (skillText, context) => {
    // 召喚時効果はトリガーで処理されるため、cardEffectsでは何もしない
    return false;
  },

  /**
   * C0000127: ゴシック・ローズ
   * 【召喚時】デッキから《ゴシック》または《鎖》魔法カード1枚を手札に加える
   */
  C0000127: (skillText, context) => {
    if (skillText.includes('【召喚時】')) {
      return searchCard(context, (card) => {
        return card.type === 'magic' &&
               !!card.name && (card.name.includes('ゴシック') || card.name.includes('鎖'));
      }) !== null;
    }
    return false;
  },

  /**
   * C0000128: 鎖縛のメイド
   * 【召喚時】相手モンスター1体の攻撃力をターン終了時まで0にする
   */
  C0000128: (skillText, context) => {
    const { addLog } = context;
    const { opponentField, setOpponentField } = getPlayerContext(context);

    if (skillText.includes('【召喚時】')) {
      const monsters = opponentField.filter(m => m !== null);
      if (monsters.length === 0) {
        addLog('相手モンスターがいません', 'info');
        return true;
      }

      const target = monsters[0];
      setOpponentField(prev => prev.map(m => {
        if (m && m.uniqueId === target.uniqueId) {
          addLog(`${m.name}の攻撃力を0にした`, 'info');
          return { ...m, attack: 0 };
        }
        return m;
      }));

      return true;
    }
    return false;
  },

  /**
   * C0000012: 粘液の増殖
   * 場にいる《粘液獣》1体を分裂させる（攻撃力は半分になる）。
   */
  C0000012: (skillText, context) => {
    const { addLog } = context;
    const { myField, setMyField, currentPlayer } = getPlayerContext(context);

    // 《粘液獣》と名の付くモンスターを探す（名前判定）
    const slimeIndex = myField.findIndex(
      (m) => m && m.name && m.name.includes('粘液獣')
    );

    if (slimeIndex === -1) {
      addLog('粘液の増殖: 場に《粘液獣》がいません', 'info');
      return false;
    }

    // 空きスロットを探す
    const emptySlotIndex = myField.findIndex((slot) => slot === null);
    if (emptySlotIndex === -1) {
      addLog('粘液の増殖: 場が満杯のため分裂できない', 'info');
      return false;
    }

    const targetSlime = myField[slimeIndex];
    if (!targetSlime) {
      addLog('粘液の増殖: 粘液獣が見つかりません', 'info');
      return false;
    }

    // 分裂: 攻撃力半分のコピーを生成
    const copy = {
      ...targetSlime,
      uniqueId: `${targetSlime.id}_magic_split_${Date.now()}`,
      attack: Math.floor((targetSlime.attack ?? 0) / 2),
      currentAttack: Math.floor((targetSlime.currentAttack ?? targetSlime.attack ?? 0) / 2),
      hp: targetSlime.hp ?? 0,
      currentHp: targetSlime.currentHp ?? targetSlime.hp ?? 0,
      canAttack: targetSlime.canAttack, // 分裂前と同じ状態を引き継ぐ
      charges: [],
      statusEffects: [],
      owner: currentPlayer,
    };

    const newField = [...myField];
    newField[emptySlotIndex] = copy;
    setMyField(newField);

    addLog(
      `粘液の増殖: ${targetSlime.name}が分裂した！（攻撃力${copy.currentAttack}）`,
      'info'
    );
    return true;
  },

  /**
   * C0000013: 粘液の罠
   * 【刹那詠唱】相手モンスター攻撃時、発動可能。
   * そのモンスターの攻撃力を800ダウン、次のターンまで行動不能にする。
   */
  C0000013: (skillText, context) => {
    const { addLog, attacker, attackerIndex } = context;
    const { setOpponentField } = getPlayerContext(context);

    // 刹那詠唱で発動された場合のみ有効
    // attacker は攻撃宣言をしたモンスター（相手フィールドにいる）
    if (!attacker || attackerIndex === undefined) {
      addLog('粘液の罠: 攻撃対象がありません', 'info');
      return false;
    }

    // 攻撃者は「相手フィールド」にいる
    // currentPlayer = 刹那詠唱使用者
    // 攻撃者は currentPlayer の相手のフィールドにいる

    // 攻撃力を800ダウン & STUN（行動不能）を付与
    setOpponentField(prev => prev.map((m, idx) => {
      if (idx === attackerIndex && m) {
        const newAttack = Math.max(0, (m.currentAttack ?? m.attack ?? 0) - 800);
        const updatedMonster = {
          ...m,
          currentAttack: newAttack,
        };

        // STUN状態異常を付与（次のターンまで行動不能）
        statusEffectEngine.applyStatus(updatedMonster, STATUS_EFFECT_TYPES.STUN, {
          duration: 1, // 次のターンまで
          source: 'C0000013',
          sourceName: '粘液の罠',
        });

        addLog(`粘液の罠: ${m.name}の攻撃力を800ダウン（${m.currentAttack || m.attack}→${newAttack}）`, 'info');
        addLog(`粘液の罠: ${m.name}は次のターンまで行動不能！`, 'info');

        return updatedMonster;
      }
      return m;
    }));

    return true;
  },

  /**
   * C0000014: 粘液再生
   * 墓地の《粘液獣》2体を場に戻す（攻撃力半分・HP半分・効果無効になる）。
   */
  C0000014: (skillText, context) => {
    const { addLog, summoningSickness = true } = context;
    const { myField, myGraveyard, setMyField, setMyGraveyard, currentPlayer } =
      getPlayerContext(context);

    // 墓地から《粘液獣》と名の付くモンスターを探す
    const slimesInGraveyard = myGraveyard.filter(
      (card) => card.type === 'monster' && card.name && card.name.includes('粘液獣')
    );

    if (slimesInGraveyard.length === 0) {
      addLog('粘液再生: 墓地に《粘液獣》がいません', 'info');
      return false;
    }

    // 空きスロットを探す
    const emptySlots = myField
      .map((slot, idx) => (slot === null ? idx : -1))
      .filter((idx) => idx !== -1);

    if (emptySlots.length === 0) {
      addLog('粘液再生: 場に空きがありません', 'info');
      return false;
    }

    // 最大2体まで蘇生
    const reviveCount = Math.min(slimesInGraveyard.length, emptySlots.length, 2);
    const toRevive = slimesInGraveyard.slice(0, reviveCount);
    const revivedIds = toRevive.map((c) => c.uniqueId || c.id);

    // 墓地から削除
    setMyGraveyard((prev) =>
      prev.filter((card) => !revivedIds.includes(card.uniqueId || card.id))
    );

    // フィールドに配置（攻撃力半分・HP半分・効果無効）
    setMyField((prev) => {
      const newField = [...prev];
      toRevive.forEach((card, i) => {
        const slotIndex = emptySlots[i];
        const halfAttack = Math.floor((card.attack ?? 0) / 2);
        const halfHp = Math.floor((card.hp ?? 0) / 2);
        newField[slotIndex] = {
          ...card,
          uniqueId: `${card.id}_revive_${Date.now()}_${i}`,
          attack: halfAttack,
          currentAttack: halfAttack,
          hp: halfHp,
          currentHp: halfHp,
          canAttack: !summoningSickness, // 召喚酔い設定に依存
          charges: [],
          statusEffects: [],
          owner: currentPlayer,
          effectNegated: true, // 効果無効
        };
        addLog(
          `粘液再生: ${card.name}を蘇生（ATK${halfAttack}/HP${halfHp}・効果無効）`,
          'info'
        );
      });
      return newField;
    });

    return true;
  },

  /**
   * C0000015: 粘液の浸食
   * 【刹那詠唱】相手の場にいるモンスター1体の攻撃力を毎ターン500ダウン、0になったら破壊する。
   */
  C0000015: (skillText, context) => {
    const { addLog } = context;
    const { opponentField } = getPlayerContext(context);

    // 相手モンスターがいるかチェック
    const hasTarget = opponentField.some((m) => m !== null);
    if (!hasTarget) {
      addLog('粘液の浸食: 対象となる相手モンスターがいません', 'info');
      return false;
    }

    // CORRODE（深蝕）状態異常を付与
    // 毎エンドフェイズATKダウン、0になったら破壊
    selectAndApplyStatusToOpponent(
      context,
      STATUS_EFFECT_TYPES.CORRODE,
      {
        duration: -1, // 永続（ATKが0になるまで）
        value: 500, // 毎ターン500ダウン
        source: 'C0000015',
        sourceName: '粘液の浸食',
      },
      '粘液の浸食'
    );

    return true;
  },

  /**
   * C0000016: 粘液爆発
   * 場にいる《粘液獣》1体を破壊し、相手モンスター全体にその攻撃力分のダメージを与える。
   */
  C0000016: (skillText, context) => {
    const { addLog } = context;
    const { myField, setMyField, setMyGraveyard, opponentField, setOpponentField } =
      getPlayerContext(context);

    // 《粘液獣》と名の付くモンスターを探す
    const slimeIndex = myField.findIndex(
      (m) => m && m.name && m.name.includes('粘液獣')
    );

    if (slimeIndex === -1) {
      addLog('粘液爆発: 場に《粘液獣》がいません', 'info');
      return false;
    }

    const targetSlime = myField[slimeIndex];
    if (!targetSlime) {
      addLog('粘液爆発: 粘液獣が見つかりません', 'info');
      return false;
    }
    const damage = targetSlime.currentAttack ?? targetSlime.attack ?? 0;

    // 粘液獣を破壊して墓地へ
    setMyField((prev) => {
      const newField = [...prev];
      newField[slimeIndex] = null;
      return newField;
    });
    setMyGraveyard((prev) => [...prev, targetSlime]);
    addLog(`粘液爆発: ${targetSlime.name}を破壊！`, 'damage');

    // 相手モンスター全体にダメージ
    const destroyedMonsters: MonsterInstance[] = [];
    setOpponentField((prev) => {
      return prev.map((monster) => {
        if (monster) {
          const newHp = (monster.currentHp ?? monster.hp ?? 0) - damage;
          addLog(
            `粘液爆発: ${monster.name}に${damage}ダメージ！`,
            'damage'
          );
          if (newHp <= 0) {
            destroyedMonsters.push(monster);
            return null;
          }
          return { ...monster, currentHp: newHp };
        }
        return monster;
      });
    });

    // 破壊されたモンスターを相手の墓地へ
    if (destroyedMonsters.length > 0) {
      const { setOpponentGraveyard } = getPlayerContext(context);
      setOpponentGraveyard((prev) => [...prev, ...destroyedMonsters]);
      destroyedMonsters.forEach((m) => {
        addLog(`${m.name}は破壊された！`, 'damage');
      });
    }

    return true;
  },

  /**
   * C0000017: 未来の粘液回路
   * デッキから《粘液獣・キング》または《新・超覚醒粘液獣－ハイパー－》を場に直接召喚。
   */
  C0000017: (skillText, context) => {
    const { addLog, summoningSickness = true } = context;
    const { myField, myDeck, setMyField, setMyDeck, currentPlayer } =
      getPlayerContext(context);

    // 空きスロットを探す
    const emptySlotIndex = myField.findIndex((slot) => slot === null);
    if (emptySlotIndex === -1) {
      addLog('未来の粘液回路: 場に空きがありません', 'info');
      return false;
    }

    // デッキから対象カードを探す
    const targetIndex = myDeck.findIndex(
      (card) =>
        card.name === '粘液獣・キング' ||
        card.name === '新・超覚醒粘液獣－ハイパー－'
    );

    if (targetIndex === -1) {
      addLog(
        '未来の粘液回路: デッキに対象カードがありません',
        'info'
      );
      return false;
    }

    const targetCard = myDeck[targetIndex];

    // デッキから削除
    setMyDeck((prev) => {
      const newDeck = [...prev];
      newDeck.splice(targetIndex, 1);
      return newDeck;
    });

    // フィールドに直接召喚
    setMyField((prev) => {
      const newField = [...prev];
      newField[emptySlotIndex] = {
        ...targetCard,
        uniqueId: `${targetCard.id}_special_summon_${Date.now()}`,
        currentAttack: targetCard.attack ?? 0,
        currentHp: targetCard.hp ?? 0,
        canAttack: !summoningSickness, // 召喚酔い設定に依存
        charges: [],
        statusEffects: [],
        owner: currentPlayer,
      };
      return newField;
    });

    addLog(
      `未来の粘液回路: ${targetCard.name}を直接召喚！`,
      'info'
    );

    return true;
  },

  /**
   * C0000430: 狼王の咆哮
   * 墓地の狼カード1枚を手札に戻す。そのカードのコストを1下げる。
   */
  C0000430: (skillText, context) => {
    const { addLog, setPendingGraveyardSelection } = context;
    const { myGraveyard, setMyGraveyard, myHand, setMyHand } = getPlayerContext(context);

    // 墓地から狼カードを探す（名前に「狼」または「ウルフ」を含む、またはカテゴリが【狼】）
    const wolfCardsInGraveyard = myGraveyard.filter(
      (card) => {
        const isWolfByName = card.name && (card.name.includes('狼') || card.name.includes('ウルフ'));
        const isWolfByCategory = card.category && card.category.includes('【狼】');
        return isWolfByName || isWolfByCategory;
      }
    );

    if (wolfCardsInGraveyard.length === 0) {
      addLog('狼王の咆哮: 墓地に狼カードがありません', 'info');
      return false;
    }

    // 1枚しかない場合は自動選択
    if (wolfCardsInGraveyard.length === 1) {
      const selectedCard = wolfCardsInGraveyard[0];

      // 墓地から削除
      setMyGraveyard(prev => prev.filter(c => c.uniqueId !== selectedCard.uniqueId));

      // 手札に追加（コスト-1）
      const cardWithReducedCost = {
        ...selectedCard,
        cost: Math.max(0, selectedCard.cost - 1),
        costReduced: true,
        costReductionSource: '狼王の咆哮',
      };
      setMyHand(prev => [...prev, cardWithReducedCost]);

      addLog(`狼王の咆哮: ${selectedCard.name}を手札に戻した！（コスト${selectedCard.cost}→${cardWithReducedCost.cost}）`, 'info');
      return true;
    }

    // 複数ある場合は選択UIを表示
    setPendingGraveyardSelection({
      cards: wolfCardsInGraveyard,
      count: 1,
      message: '手札に戻す狼カードを選択してください',
      callback: (selectedCards: CardWithId[]) => {
        if (!selectedCards || selectedCards.length === 0) return;
        const selectedCard = selectedCards[0];

        // 墓地から削除
        setMyGraveyard(prev => prev.filter(c => c.uniqueId !== selectedCard.uniqueId));

        // 手札に追加（コスト-1）
        const cardWithReducedCost = {
          ...selectedCard,
          cost: Math.max(0, selectedCard.cost - 1),
          costReduced: true,
          costReductionSource: '狼王の咆哮',
        };
        setMyHand(prev => [...prev, cardWithReducedCost]);

        addLog(`狼王の咆哮: ${selectedCard.name}を手札に戻した！（コスト${selectedCard.cost}→${cardWithReducedCost.cost}）`, 'info');
      },
      selectionOwner: context.currentPlayer,
    });

    return true;
  },

  /**
   * C0000131: 鎖の守護者
   * 基本技：自分の《ゴシック》モンスター1体のHPを500回復する。
   * ※召喚時トリガーは cardTriggers/primitiveCards.js に実装済み
   */
  C0000131: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 自分の場の《ゴシック》モンスターを取得
      const gothicMonsters = myField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster && monster.name && monster.name.includes('ゴシック'));

      if (gothicMonsters.length === 0) {
        addLog('場に《ゴシック》モンスターがいません', 'info');
        return true;
      }

      // 1体のみの場合は自動選択
      if (gothicMonsters.length === 1) {
        const target = gothicMonsters[0];
        setMyField(prev => prev.map((m, idx) => {
          if (idx === target.index && m) {
            const newHp = Math.min(m.hp ?? 0, (m.currentHp ?? m.hp ?? 0) + 500);
            addLog(`鎖の守護者の基本技: ${m.name}のHPを500回復！（HP: ${m.currentHp || m.hp} → ${newHp}）`, 'heal');
            return { ...m, currentHp: newHp };
          }
          return m;
        }));
        return true;
      }

      // 複数の場合はターゲット選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: 'HPを500回復する《ゴシック》モンスターを選択',
          targetPlayer: 'self',
          validTargets: gothicMonsters.map(t => t.index),
          callback: (targetIndex: number) => {
            setMyField(prev => prev.map((m, idx) => {
              if (idx === targetIndex && m) {
                const newHp = Math.min(m.hp ?? 0, (m.currentHp ?? m.hp ?? 0) + 500);
                addLog(`鎖の守護者の基本技: ${m.name}のHPを500回復！（HP: ${m.currentHp || m.hp} → ${newHp}）`, 'heal');
                return { ...m, currentHp: newHp };
              }
              return m;
            }));
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      // フォールバック
      const target = gothicMonsters[0];
      setMyField(prev => prev.map((m, idx) => {
        if (idx === target.index && m) {
          const newHp = Math.min(m.hp ?? 0, (m.currentHp ?? m.hp ?? 0) + 500);
          addLog(`鎖の守護者の基本技: ${m.name}のHPを500回復！`, 'heal');
          return { ...m, currentHp: newHp };
        }
        return m;
      }));
      return true;
    }
    return false;
  },

  /**
   * C0000138: 禁忌のゴシッククラウン
   * 基本技：相手の場のカードを全て墓地に送り、その枚数×500ダメージを相手プレイヤーに与える、このターン自分は攻撃できない。
   * ※常時効果・自壊時トリガーは別ファイルに実装済み
   */
  C0000138: (skillText, context) => {
    const { addLog, monsterIndex } = context;
    const { myField, setMyField, opponentField, setOpponentField, setOpponentGraveyard, setOpponentLife } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 相手の場のモンスターを数える
      const opponentMonsters = opponentField.filter(m => m !== null);
      const destroyCount = opponentMonsters.length;

      if (destroyCount === 0) {
        addLog('禁忌のゴシッククラウンの基本技: 相手の場にモンスターがいません', 'info');
        // このターン攻撃不可にする
        setMyField(prev => prev.map((m, idx) => {
          if (idx === monsterIndex && m) {
            return { ...m, canAttack: false };
          }
          return m;
        }));
        return true;
      }

      // 相手モンスターを全て墓地に送る
      setOpponentGraveyard(prev => [...prev, ...opponentMonsters]);
      setOpponentField([null, null, null, null, null]);

      // ダメージ計算
      const damage = destroyCount * 500;
      setOpponentLife(prev => prev - damage);

      addLog(`禁忌のゴシッククラウンの基本技: 相手モンスター${destroyCount}体を破壊！${damage}ダメージ！`, 'damage');

      // このターン攻撃不可にする
      setMyField(prev => prev.map((m, idx) => {
        if (idx === monsterIndex && m) {
          addLog('禁忌のゴシッククラウン: このターン攻撃できない', 'info');
          return { ...m, canAttack: false };
        }
        return m;
      }));

      return true;
    }
    return false;
  },

  /**
   * C0000125: ゴシック・ドール
   * 基本技：コスト2以下の相手のモンスター1体を操って味方にする（ターン終了時まで）。
   */
  C0000125: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField, opponentField, setOpponentField, currentPlayer } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // コスト2以下の相手モンスターを取得
      const validTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster && monster.cost <= 2);

      if (validTargets.length === 0) {
        addLog('ゴシック・ドールの基本技: コスト2以下の相手モンスターがいません', 'info');
        return true;
      }

      // 自分の場に空きがあるか確認
      const emptySlot = myField.findIndex(s => s === null);
      if (emptySlot === -1) {
        addLog('ゴシック・ドールの基本技: 自分の場に空きがありません', 'info');
        return true;
      }

      const controlMonster = (targetIndex: number) => {
        const target = opponentField[targetIndex];
        if (!target) return;

        // 相手フィールドから除去
        setOpponentField(prev => {
          const newField = [...prev];
          newField[targetIndex] = null;
          return newField;
        });

        // 自分フィールドに追加（ターン終了時まで）
        const controlledMonster = {
          ...target,
          controlledUntilEndOfTurn: true,
          originalOwner: (currentPlayer === 1 ? 2 : 1) as PlayerNumber,
          owner: currentPlayer,
          canAttack: true, // コントロール奪取したモンスターは攻撃可能
        };

        setMyField(prev => {
          const newField = [...prev];
          const slot = newField.findIndex(s => s === null);
          if (slot !== -1) {
            newField[slot] = controlledMonster;
          }
          return newField;
        });

        addLog(`ゴシック・ドールの基本技: ${target.name}のコントロールを奪った！（ターン終了時まで）`, 'info');
      };

      // 1体のみの場合は自動選択
      if (validTargets.length === 1) {
        controlMonster(validTargets[0].index);
        return true;
      }

      // 複数の場合はターゲット選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: 'コントロールを奪う相手モンスターを選択（コスト2以下）',
          targetPlayer: 'opponent',
          validTargets: validTargets.map(t => t.index),
          callback: controlMonster,
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      // フォールバック
      controlMonster(validTargets[0].index);
      return true;
    }
    return false;
  },

  /**
   * C0000132: ゴシック・パペット
   * 基本技：このカードをリリースし、次の《ゴシック》モンスターの召喚コストを2軽減。
   */
  C0000132: (skillText, context) => {
    const { addLog, monsterIndex, setNextSummonCostReduction } = context;
    const { myField, setMyField, myGraveyard, setMyGraveyard } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      const monster = myField[monsterIndex];
      if (!monster) {
        addLog('ゴシック・パペットの基本技: モンスターが存在しません', 'info');
        return false;
      }

      // 自身をリリース（墓地に送る）
      setMyField(prev => {
        const newField = [...prev];
        newField[monsterIndex] = null;
        return newField;
      });
      setMyGraveyard(prev => [...prev, monster]);

      // 次の《ゴシック》モンスターのコストを2軽減するフラグを立てる
      if (setNextSummonCostReduction) {
        setNextSummonCostReduction({
          nameIncludes: 'ゴシック',
          reduction: 2,
          source: 'ゴシック・パペット',
        });
      }

      addLog('ゴシック・パペットの基本技: 自身をリリースし、次の《ゴシック》モンスターの召喚コストを2軽減！', 'info');
      return true;
    }
    return false;
  },

  /**
   * C0000133: 鎖の舞踏
   * 場にいるモンスター1体の攻撃力をターン終了時まで0にし、そのモンスターが破壊されるまで、毎ターン相手プレイヤーに300ダメージを与える。
   */
  C0000133: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField, myField, setMyField } = getPlayerContext(context);

    // 全フィールドのモンスターを取得
    const allMonsters = [
      ...opponentField.map((m, idx) => ({ monster: m, index: idx, isOpponent: true })),
      ...myField.map((m, idx) => ({ monster: m, index: idx, isOpponent: false })),
    ].filter(({ monster }) => monster !== null);

    if (allMonsters.length === 0) {
      addLog('鎖の舞踏: 場にモンスターがいません', 'info');
      return false;
    }

    const applyChainDance = (targetIndex: number, isOpponent: boolean) => {
      const setField = isOpponent ? setOpponentField : setMyField;
      const field = isOpponent ? opponentField : myField;
      const target = field[targetIndex];

      if (!target) return;

      setField(prev => prev.map((m, idx) => {
        if (idx === targetIndex && m) {
          return {
            ...m,
            currentAttack: 0,
            chainDanceEffect: true, // 毎ターン300ダメージフラグ
          };
        }
        return m;
      }));

      addLog(`鎖の舞踏: ${target.name}の攻撃力を0に！毎ターン相手に300ダメージ！`, 'info');
    };

    // ターゲット選択UI（相手モンスター優先）
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '攻撃力を0にするモンスターを選択',
        targetPlayer: 'opponent',
        callback: (targetIndex: number) => {
          applyChainDance(targetIndex, true);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック: 相手モンスターがいれば最初の1体
    const opponentTargets = allMonsters.filter(t => t.isOpponent);
    if (opponentTargets.length > 0) {
      applyChainDance(opponentTargets[0].index, true);
    } else {
      applyChainDance(allMonsters[0].index, allMonsters[0].isOpponent);
    }
    return true;
  },

  /**
   * C0000134: ゴシックの誘惑
   * 相手モンスター1体を手札に戻す。
   */
  C0000134: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField, opponentHand, setOpponentHand } = getPlayerContext(context);

    // 相手フィールドのモンスターを取得
    const validTargets = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (validTargets.length === 0) {
      addLog('ゴシックの誘惑: 相手の場にモンスターがいません', 'info');
      return false;
    }

    const bounceMonster = (targetIndex: number) => {
      const target = opponentField[targetIndex];
      if (!target) return;

      // フィールドから除去
      setOpponentField(prev => {
        const newField = [...prev];
        newField[targetIndex] = null;
        return newField;
      });

      // 手札に戻す（元のカード状態に戻す）
      const returnedCard = {
        ...target,
        currentHp: undefined,
        currentAttack: undefined,
        canAttack: undefined,
        charges: undefined,
        statusEffects: undefined,
      };
      setOpponentHand(prev => [...prev, returnedCard]);

      addLog(`ゴシックの誘惑: ${target.name}を手札に戻した！`, 'info');
    };

    // 1体のみの場合は自動選択
    if (validTargets.length === 1) {
      bounceMonster(validTargets[0].index);
      return true;
    }

    // 複数の場合はターゲット選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '手札に戻す相手モンスターを選択',
        targetPlayer: 'opponent',
        callback: bounceMonster,
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック
    bounceMonster(validTargets[0].index);
    return true;
  },

  /**
   * C0000135: 檻の牢獄
   * 相手の場にいるモンスター1体を2ターン行動不能にし、そのモンスターの攻撃力の半分を相手プレイヤーにダメージとして与える。
   */
  C0000135: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField, setOpponentLife } = getPlayerContext(context);

    // 相手フィールドのモンスターを取得
    const validTargets = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (validTargets.length === 0) {
      addLog('檻の牢獄: 相手の場にモンスターがいません', 'info');
      return false;
    }

    const imprisonMonster = (targetIndex: number) => {
      const target = opponentField[targetIndex];
      if (!target) return;

      const damage = Math.floor((target.currentAttack ?? target.attack ?? 0) / 2);

      // 2ターン行動不能にする
      setOpponentField(prev => prev.map((m, idx) => {
        if (idx === targetIndex && m) {
          return {
            ...m,
            imprisonedTurns: 2, // 行動不能ターン数
            canAttack: false,
          };
        }
        return m;
      }));

      // 攻撃力の半分をダメージ
      setOpponentLife(prev => prev - damage);

      addLog(`檻の牢獄: ${target.name}を2ターン行動不能に！${damage}ダメージ！`, 'damage');
    };

    // 1体のみの場合は自動選択
    if (validTargets.length === 1) {
      imprisonMonster(validTargets[0].index);
      return true;
    }

    // 複数の場合はターゲット選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '行動不能にする相手モンスターを選択',
        targetPlayer: 'opponent',
        callback: imprisonMonster,
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック
    imprisonMonster(validTargets[0].index);
    return true;
  },

  /**
   * C0000136: 鎖の呪縛
   * 相手モンスター1体に1500ダメージを与える。
   */
  C0000136: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField, opponentGraveyard, setOpponentGraveyard } = getPlayerContext(context);

    // 相手フィールドのモンスターを取得
    const validTargets = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (validTargets.length === 0) {
      addLog('鎖の呪縛: 相手の場にモンスターがいません', 'info');
      return false;
    }

    const damageMonster = (targetIndex: number) => {
      const target = opponentField[targetIndex];
      if (!target) return;

      const newHp = (target.currentHp ?? target.hp ?? 0) - 1500;

      setOpponentField(prev => {
        const newField = [...prev];
        if (newHp <= 0) {
          // 破壊
          setOpponentGraveyard(prevGrave => [...prevGrave, target]);
          newField[targetIndex] = null;
          addLog(`鎖の呪縛: ${target.name}に1500ダメージ！破壊！`, 'damage');
        } else {
          newField[targetIndex] = { ...target, currentHp: newHp };
          addLog(`鎖の呪縛: ${target.name}に1500ダメージ！（残HP: ${newHp}）`, 'damage');
        }
        return newField;
      });
    };

    // 1体のみの場合は自動選択
    if (validTargets.length === 1) {
      damageMonster(validTargets[0].index);
      return true;
    }

    // 複数の場合はターゲット選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '1500ダメージを与える相手モンスターを選択',
        targetPlayer: 'opponent',
        callback: damageMonster,
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック
    damageMonster(validTargets[0].index);
    return true;
  },

  /**
   * C0000137: ゴシック・レクイエム
   * 場にいる《ゴシック》モンスター1体を破壊し、その攻撃力の2倍のダメージを相手モンスター全体に与える
   */
  C0000137: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField, myGraveyard, setMyGraveyard, opponentField, setOpponentField, opponentGraveyard, setOpponentGraveyard } = getPlayerContext(context);

    // 自分の場の《ゴシック》モンスターを取得
    const gothicMonsters = myField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster && monster.name && monster.name.includes('ゴシック'));

    if (gothicMonsters.length === 0) {
      addLog('ゴシック・レクイエム: 場に《ゴシック》モンスターがいません', 'info');
      return false;
    }

    const performRequiem = (targetIndex: number) => {
      const target = myField[targetIndex];
      if (!target) return;

      const damage = (target.currentAttack ?? target.attack ?? 0) * 2;

      // 《ゴシック》モンスターを破壊
      setMyField(prev => {
        const newField = [...prev];
        newField[targetIndex] = null;
        return newField;
      });
      setMyGraveyard(prev => [...prev, target]);

      addLog(`ゴシック・レクイエム: ${target.name}を破壊！`, 'info');

      // 相手モンスター全体にダメージ
      const destroyedMonsters: MonsterInstance[] = [];
      setOpponentField(prev => prev.map(m => {
        if (m) {
          const newHp = (m.currentHp ?? m.hp ?? 0) - damage;
          if (newHp <= 0) {
            destroyedMonsters.push(m);
            return null;
          }
          return { ...m, currentHp: newHp };
        }
        return m;
      }));

      if (destroyedMonsters.length > 0) {
        setOpponentGraveyard(prev => [...prev, ...destroyedMonsters]);
      }

      addLog(`ゴシック・レクイエム: 相手モンスター全体に${damage}ダメージ！${destroyedMonsters.length > 0 ? `（${destroyedMonsters.length}体破壊）` : ''}`, 'damage');
    };

    // 1体のみの場合は自動選択
    if (gothicMonsters.length === 1) {
      performRequiem(gothicMonsters[0].index);
      return true;
    }

    // 複数の場合はターゲット選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '破壊する《ゴシック》モンスターを選択',
        targetPlayer: 'self',
        validTargets: gothicMonsters.map(t => t.index),
        callback: performRequiem,
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック
    performRequiem(gothicMonsters[0].index);
    return true;
  },

  /**
   * C0000238: おしおきの鎖（魔法カード）【刹那詠唱】
   * 相手モンスター1体の攻撃力を800下げ、ターン終了時まで行動不能。
   */
  C0000238: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField } = getPlayerContext(context);
    const { statusEffectEngine, STATUS_EFFECT_TYPES } = require('../statusEffects');

    // 相手の場にモンスターがいるか確認
    const targetableMonsters = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (targetableMonsters.length === 0) {
      addLog('おしおきの鎖: 相手の場にモンスターがいません', 'info');
      return false;
    }

    const applyEffect = (selectedIndex: number) => {
      setOpponentField(prev => prev.map((m, idx) => {
        if (idx === selectedIndex && m) {
          // 攻撃力800ダウン
          const newAttack = Math.max(0, (m.currentAttack ?? m.attack ?? 0) - 800);
          addLog(`おしおきの鎖: ${m.name}の攻撃力-800！（${m.currentAttack || m.attack} → ${newAttack}）`, 'info');

          // 行動不能（STUN状態）を付与
          const updatedMonster = {
            ...m,
            currentAttack: newAttack,
          };

          // 状態異常を付与
          statusEffectEngine.applyStatus(updatedMonster, STATUS_EFFECT_TYPES.STUN, {
            duration: 0, // このターン終了時まで
            source: 'C0000238',
            sourceName: 'おしおきの鎖',
          });

          addLog(`おしおきの鎖: ${m.name}は行動不能になった！`, 'info');
          return updatedMonster;
        }
        return m;
      }));
    };

    // 1体のみの場合は自動選択
    if (targetableMonsters.length === 1) {
      applyEffect(targetableMonsters[0].index);
      return true;
    }

    // 複数の場合はターゲット選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '攻撃力を下げて行動不能にするモンスターを選択',
        targetPlayer: 'opponent',
        callback: (selectedIndex: number) => {
          applyEffect(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック
    applyEffect(targetableMonsters[0].index);
    return true;
  },

  // ========================================
  // 鎖縛の姫リアノン関連カード
  // ========================================

  /**
   * C0000244: 鎖縛の幻姫リアノン
   * 基本技：相手モンスター1体の効果を無効化し、次のターン終了時までそのモンスターを行動不能にする。
   */
  C0000244: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 相手モンスターを取得
      const targetableMonsters = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null);

      if (targetableMonsters.length === 0) {
        addLog('鎖縛の幻姫リアノンの基本技: 相手の場にモンスターがいません', 'info');
        return false;
      }

      const applyEffect = (targetIndex: number) => {
        setOpponentField(prev => prev.map((m, idx) => {
          if (idx === targetIndex && m) {
            addLog(`鎖縛の幻姫リアノンの基本技: ${m.name}の効果を無効化し、行動不能にした！`, 'info');
            return {
              ...m,
              effectDisabled: true,
              canAttack: false,
              actionDisabledUntilTurn: 2, // 次のターン終了時まで
            };
          }
          return m;
        }));
      };

      // 1体のみの場合は自動選択
      if (targetableMonsters.length === 1) {
        applyEffect(targetableMonsters[0].index);
        return true;
      }

      // 複数の場合は選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: '効果無効＆行動不能にするモンスターを選択',
          targetPlayer: 'opponent',
          callback: (selectedIndex: number) => {
            applyEffect(selectedIndex);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      // フォールバック
      applyEffect(targetableMonsters[0].index);
      return true;
    }

    return false;
  },

  /**
   * C0000246: 鎖縛の禁忌姫リアノン・エターナル
   * 【召喚時】相手モンスター1体を1ターン行動不能にする。
   * 【常時】相手の魔法カードを1ターンに1度無効化する。
   * 【自壊時】2000ダメージを受ける。
   * 基本技：デッキか墓地から『鎖縛の幻姫リアノン』を特殊召喚する。
   * ※召喚時・常時・自壊時はトリガー・常時効果で実装済み
   */
  C0000246: (skillText, context) => {
    const { addLog, setPendingGraveyardSelection, summoningSickness = true } = context;
    const { myDeck, setMyDeck, myGraveyard, setMyGraveyard, myField, setMyField, currentPlayer } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 空きスロットを確認
      const emptySlotIndex = myField.findIndex(slot => slot === null);
      if (emptySlotIndex === -1) {
        addLog('鎖縛の禁忌姫リアノン・エターナルの基本技: 場に空きがありません', 'info');
        return false;
      }

      // 墓地から『鎖縛の幻姫リアノン』を探す
      const rianonInGraveyard = myGraveyard.find(c =>
        c.name === '鎖縛の幻姫リアノン' || c.id === 'C0000244'
      );

      // デッキから『鎖縛の幻姫リアノン』を探す
      const rianonInDeckIndex = myDeck.findIndex(c =>
        c.name === '鎖縛の幻姫リアノン' || c.id === 'C0000244'
      );

      if (!rianonInGraveyard && rianonInDeckIndex === -1) {
        addLog('鎖縛の禁忌姫リアノン・エターナルの基本技: デッキ・墓地に『鎖縛の幻姫リアノン』がいません', 'info');
        return false;
      }

      // 両方にある場合は墓地を優先
      const summonFromGraveyard = (card: CardWithId) => {
        setMyGraveyard(prev => prev.filter(c => c.uniqueId !== card.uniqueId));
        setMyField(prev => {
          const newField = [...prev];
          const slot = newField.findIndex(s => s === null);
          if (slot !== -1) {
            newField[slot] = {
              ...card,
              uniqueId: `${card.id}_revived_${Date.now()}`,
              currentAttack: card.attack ?? 0,
              currentHp: card.hp ?? 0,
              canAttack: !summoningSickness, // 召喚酔い設定に依存
              charges: [],
              statusEffects: [],
              owner: currentPlayer,
            };
          }
          return newField;
        });
        addLog(`鎖縛の禁忌姫リアノン・エターナルの基本技: 墓地から「${card.name}」を特殊召喚！`, 'info');
      };

      const summonFromDeck = (deckIndex: number) => {
        const card = myDeck[deckIndex];
        setMyDeck(prev => prev.filter((_, idx) => idx !== deckIndex));
        setMyField(prev => {
          const newField = [...prev];
          const slot = newField.findIndex(s => s === null);
          if (slot !== -1) {
            newField[slot] = {
              ...card,
              uniqueId: `${card.id}_summoned_${Date.now()}`,
              currentAttack: card.attack ?? 0,
              currentHp: card.hp ?? 0,
              canAttack: !summoningSickness, // 召喚酔い設定に依存
              charges: [],
              statusEffects: [],
              owner: currentPlayer,
            };
          }
          return newField;
        });
        addLog(`鎖縛の禁忌姫リアノン・エターナルの基本技: デッキから「${card.name}」を特殊召喚！`, 'info');
      };

      // 墓地を優先
      if (rianonInGraveyard) {
        summonFromGraveyard(rianonInGraveyard);
      } else {
        summonFromDeck(rianonInDeckIndex);
      }

      return true;
    }

    return false;
  },

  /**
   * C0000247: 鎖の共鳴
   * 場に『鎖縛の幻姫リアノン』または『鎖縛の禁忌姫リアノン・エターナル』がいる場合、
   * その攻撃力をターン終了時まで1000アップし、相手モンスター1体をターン終了時まで行動不能にする
   */
  C0000247: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField, opponentField, setOpponentField } = getPlayerContext(context);

    // 場に『鎖縛の幻姫リアノン』または『鎖縛の禁忌姫リアノン・エターナル』がいるか確認
    const rianonOnField = myField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster && monster.name &&
        (monster.name === '鎖縛の幻姫リアノン' || monster.name === '鎖縛の禁忌姫リアノン・エターナル'));

    if (rianonOnField.length === 0) {
      addLog('鎖の共鳴: 場に『鎖縛の幻姫リアノン』または『鎖縛の禁忌姫リアノン・エターナル』がいません', 'info');
      return false;
    }

    // リアノンの攻撃力を1000アップ
    const rianon = rianonOnField[0];
    setMyField(prev => {
      const newField = [...prev];
      const monster = newField[rianon.index];
      if (monster) {
        const newAtk = (monster.currentAttack ?? monster.attack ?? 0) + 1000;
        newField[rianon.index] = {
          ...monster,
          currentAttack: newAtk,
          atkBuffedUntilEndOfTurn: true,
        };
        addLog(`鎖の共鳴: ${monster.name}の攻撃力が1000アップ！（${newAtk}）`, 'info');
      }
      return newField;
    });

    // 相手フィールドにモンスターがいるか確認
    const opponentMonsters = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (opponentMonsters.length === 0) {
      addLog('鎖の共鳴: 相手フィールドにモンスターがいません', 'info');
      return true; // リアノンのバフは既に適用済み
    }

    // 相手モンスターを行動不能にする
    const disableMonster = (targetIndex: number) => {
      setOpponentField(prev => {
        const newField = [...prev];
        if (newField[targetIndex]) {
          newField[targetIndex] = {
            ...newField[targetIndex],
            canAttack: false,
            actionDisabledUntilEndOfTurn: true,
          };
          addLog(`鎖の共鳴: ${newField[targetIndex].name}がターン終了時まで行動不能！`, 'info');
        }
        return newField;
      });
    };

    // 1体のみの場合は自動選択
    if (opponentMonsters.length === 1) {
      disableMonster(opponentMonsters[0].index);
      return true;
    }

    // 複数いる場合は選択UI
    setPendingMonsterTarget({
      message: '行動不能にする相手モンスターを選択',
      targetPlayer: 'opponent',
      callback: (selectedIndex: number) => {
        disableMonster(selectedIndex);
      },
      selectionOwner: context.currentPlayer,
    });

    return true;
  },

  // ========================================
  // ヴォランティス関連カード
  // ========================================

  /**
   * C0000353: ヴォランティス・エテルノス
   * 【犠現】: 自分の場からモンスター1体を破壊し、そのコスト分このカードのSPコストを軽減（最大3軽減）。
   * 【召喚時】相手フィールド全体に2000ダメージを与え、このターン、自分はダイレクトアタック不可。
   * 【相手の魔法カードの発動時】ターンに1度無効化。
   * 上級技: 自分のライフを2000減らし、相手モンスター全体の攻撃力を1500下げる。
   * ※召喚時効果はトリガーで実装済み
   */
  C0000353: (skillText, context) => {
    const { addLog, monsterIndex } = context;
    const { myField, setMyField, opponentField, setOpponentField, setMyLife } = getPlayerContext(context);

    // 上級技: 自分のライフを2000減らし、相手モンスター全体の攻撃力を1500下げる
    if (context.skillType === 'advanced') {
      // 自分のライフを2000減らす
      setMyLife(prev => prev - 2000);
      addLog('ヴォランティス・エテルノスの上級技: 自分のライフを2000消費！', 'damage');

      // 相手モンスター全体の攻撃力を1500下げる
      let debuffCount = 0;
      setOpponentField(prev => prev.map(monster => {
        if (monster) {
          debuffCount++;
          const currentAtk = monster.currentAttack ?? monster.attack ?? 0;
          const newAtk = Math.max(0, currentAtk - 1500);
          return {
            ...monster,
            currentAttack: newAtk,
          };
        }
        return monster;
      }));

      if (debuffCount > 0) {
        addLog(`ヴォランティス・エテルノスの上級技: 相手モンスター${debuffCount}体の攻撃力-1500！`, 'info');
      } else {
        addLog('ヴォランティス・エテルノスの上級技: 相手フィールドにモンスターがいません', 'info');
      }

      return true;
    }

    return false;
  },

  /**
   * C0000404: グリムヴァルド
   * 基本技: デッキの一番上のカード3枚を見て、1枚を手札に加え残りは好きな順番でデッキに戻す
   */
  C0000404: (skillText, context) => {
    const { addLog, setPendingDeckReview } = context;
    const { myDeck, setMyDeck, setMyHand } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      if (myDeck.length === 0) {
        addLog('グリムヴァルドの基本技: デッキがありません', 'info');
        return false;
      }

      // デッキの上から3枚（または残り全部）を取得
      const viewCount = Math.min(3, myDeck.length);
      const topCards = myDeck.slice(0, viewCount);

      // カード選択UI
      setPendingDeckReview({
        message: `デッキの上から${viewCount}枚です。手札に加えるカードを1枚選んでください`,
        cards: topCards,
        count: 1,
        callback: (selectedCards: CardWithId[]) => {
          if (!selectedCards || selectedCards.length === 0) {
            addLog('グリムヴァルドの基本技: キャンセルしました', 'info');
            return;
          }

          const selectedCard = selectedCards[0];
          const remainingCards = topCards.filter(c => c.uniqueId !== selectedCard.uniqueId);

          // 選んだカードを手札に
          setMyHand(prev => [...prev, selectedCard]);
          addLog(`グリムヴァルドの基本技: 「${selectedCard.name}」を手札に加えた！`, 'info');

          // 残りはデッキの上に戻す（順番はそのまま）
          setMyDeck(prev => [...remainingCards, ...prev.slice(viewCount)]);

          if (remainingCards.length > 0) {
            addLog(`グリムヴァルドの基本技: 残り${remainingCards.length}枚をデッキの上に戻した`, 'info');
          }
        },
        selectionOwner: context.currentPlayer,
      });

      return true;
    }

    return false;
  },

  /**
   * C0000405: アッシュドラゴン
   * 基本技: 自身のHPを500減らし、自分のデッキの上から3枚まで墓地に送る
   * 上級技: 自身を破壊し、相手モンスター1体に1500ダメージを与え、墓地の【ドラゴン】1体を手札に加える
   */
  C0000405: (skillText, context) => {
    const { addLog, setPendingMonsterTarget, setPendingGraveyardSelection, monsterIndex } = context;
    const { myField, setMyField, myDeck, setMyDeck, myGraveyard, setMyGraveyard, setMyHand, opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 自身のHPを500減らす
      const monster = myField[monsterIndex];
      if (!monster) {
        addLog('アッシュドラゴンの基本技: モンスターが存在しません', 'info');
        return false;
      }

      const newHp = monster.currentHp - 500;
      if (newHp <= 0) {
        // 自爆
        setMyField(prev => {
          const newField = [...prev];
          newField[monsterIndex] = null;
          return newField;
        });
        setMyGraveyard(prev => [...prev, monster]);
        addLog('アッシュドラゴンの基本技: 自身のHP減少で破壊された！', 'damage');
        return true;
      }

      setMyField(prev => {
        const newField = [...prev];
        newField[monsterIndex] = { ...monster, currentHp: newHp };
        return newField;
      });
      addLog(`アッシュドラゴンの基本技: 自身のHPを500減少（残HP: ${newHp}）`, 'damage');

      // デッキの上から3枚を墓地に
      const millCount = Math.min(3, myDeck.length);
      if (millCount > 0) {
        const milledCards = myDeck.slice(0, millCount);
        setMyDeck(prev => prev.slice(millCount));
        setMyGraveyard(prev => [...prev, ...milledCards]);
        addLog(`アッシュドラゴンの基本技: デッキから${millCount}枚を墓地に送った（${milledCards.map(c => c.name).join('、')}）`, 'info');
      } else {
        addLog('アッシュドラゴンの基本技: デッキがありません', 'info');
      }

      return true;
    }

    if (context.skillType === 'advanced') {
      // 相手モンスターがいるか確認
      const opponentMonsters = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null);

      if (opponentMonsters.length === 0) {
        addLog('アッシュドラゴンの上級技: 相手フィールドにモンスターがいません', 'info');
        return false;
      }

      // 自身を破壊
      const monster = myField[monsterIndex];
      if (!monster) {
        addLog('アッシュドラゴンの上級技: モンスターが存在しません', 'info');
        return false;
      }

      setMyField(prev => {
        const newField = [...prev];
        newField[monsterIndex] = null;
        return newField;
      });
      setMyGraveyard(prev => [...prev, monster]);
      addLog('アッシュドラゴンの上級技: 自身を破壊！', 'damage');

      // ダメージ適用関数
      const applyDamage = (targetIndex: number) => {
        setOpponentField(prev => {
          const newField = [...prev];
          const target = newField[targetIndex];
          if (target) {
            const newHp = target.currentHp - 1500;
            if (newHp <= 0) {
              setOpponentGraveyard(p => [...p, target]);
              addLog(`アッシュドラゴンの上級技: ${target.name}に1500ダメージで破壊！`, 'damage');
              newField[targetIndex] = null;
            } else {
              newField[targetIndex] = { ...target, currentHp: newHp };
              addLog(`アッシュドラゴンの上級技: ${target.name}に1500ダメージ！（残HP: ${newHp}）`, 'damage');
            }
          }
          return newField;
        });

        // 墓地のドラゴンを手札に加える（アッシュドラゴン自身も墓地にいる）
        const dragonsInGraveyard = myGraveyard.filter(c =>
          c.type === 'monster' && hasCategory(c, 'ドラゴン')
        );

        // 自身が墓地に行った後なので再度チェック
        setTimeout(() => {
          const updatedGraveyard = [...myGraveyard, monster];
          const allDragons = updatedGraveyard.filter(c =>
            c.type === 'monster' && hasCategory(c, 'ドラゴン')
          );

          if (allDragons.length === 0) {
            addLog('アッシュドラゴンの上級技: 墓地にドラゴンがいません', 'info');
            return;
          }

          if (allDragons.length === 1) {
            const dragon = allDragons[0];
            setMyGraveyard(prev => prev.filter(c => c.uniqueId !== dragon.uniqueId));
            setMyHand(prev => [...prev, dragon]);
            addLog(`アッシュドラゴンの上級技: 「${dragon.name}」を手札に加えた！`, 'info');
            return;
          }

          setPendingGraveyardSelection({
            message: '手札に加えるドラゴンを1体選択',
            cards: allDragons,
            count: 1,
            callback: (selectedCards: CardWithId[]) => {
              if (selectedCards && selectedCards.length > 0) {
                const dragon = selectedCards[0];
                setMyGraveyard(prev => prev.filter(c => c.uniqueId !== dragon.uniqueId));
                setMyHand(prev => [...prev, dragon]);
                addLog(`アッシュドラゴンの上級技: 「${dragon.name}」を手札に加えた！`, 'info');
              }
            },
            selectionOwner: context.currentPlayer,
          });
        }, 100);
      };

      // 1体のみなら自動選択
      if (opponentMonsters.length === 1) {
        applyDamage(opponentMonsters[0].index);
      } else {
        setPendingMonsterTarget({
          message: '1500ダメージを与える相手モンスターを選択',
          targetPlayer: 'opponent',
          callback: (selectedIndex: number) => {
            applyDamage(selectedIndex);
          },
          selectionOwner: context.currentPlayer,
        });
      }

      return true;
    }

    return false;
  },

  // ========================================
  // 深海領域対策 魔法カード
  // ========================================

  /**
   * C0000535: 大干ばつ
   * 相手の深海領域のカードを全て深度0で強制浮上させる。
   * 相手が浮上コストを払えないカードは全て破壊される。
   */
  C0000535: (skillText, context) => {
    const { addLog, summoningSickness = true } = context;
    const { opponentField, setOpponentField, setOpponentGraveyard, currentPlayer, opponentActiveSP, setOpponentActiveSP } = getPlayerContext(context);

    // 相手の深海領域を取得
    const opponentDeepSeaZone = (context as any).opponentDeepSeaZone as import('../deepSeaZone/types').DeepSeaZone | undefined;
    const setOpponentDeepSeaZone = (context as any).setOpponentDeepSeaZone as ((updater: (prev: import('../deepSeaZone/types').DeepSeaZone) => import('../deepSeaZone/types').DeepSeaZone) => void) | undefined;

    if (!opponentDeepSeaZone || !setOpponentDeepSeaZone) {
      addLog('大干ばつ: 相手の深海領域が利用できません', 'info');
      return false;
    }

    // 深海領域が開放されていない、またはカードがない場合は効果なし
    if (!opponentDeepSeaZone.isUnlocked || opponentDeepSeaZone.cards.length === 0) {
      addLog('大干ばつ: 相手の深海領域にカードがありません', 'info');
      return false;
    }

    const cards = opponentDeepSeaZone.cards;
    const emptySlots = opponentField.filter(s => s === null).length;
    let currentSP = opponentActiveSP;
    let slotsUsed = 0;

    const surfacedMonsters: import('../../types').MonsterInstance[] = [];
    const destroyedCards: import('../../types').CardWithId[] = [];

    // 各カードを深度0で強制浮上を試みる
    cards.forEach(deepSeaCard => {
      // 深度0なのでコスト = 元のコスト
      const surfaceCost = deepSeaCard.card.cost || 0;

      if (slotsUsed < emptySlots && currentSP >= surfaceCost) {
        // 浮上可能
        currentSP -= surfaceCost;
        slotsUsed++;

        const surfacedMonster: import('../../types').MonsterInstance = {
          ...deepSeaCard.card,
          uniqueId: `${deepSeaCard.card.id}_forced_surface_${Date.now()}_${Math.random()}`,
          currentAttack: deepSeaCard.card.attack ?? 0,
          currentHp: deepSeaCard.card.hp ?? 0,
          canAttack: !summoningSickness,
          charges: [],
          statusEffects: [],
          owner: (currentPlayer === 1 ? 2 : 1) as import('../../types').PlayerNumber,
          surfaceDepth: 0,
        };
        surfacedMonsters.push(surfacedMonster);
        addLog(`大干ばつ: ${deepSeaCard.card.name}が強制浮上！（コスト${surfaceCost}）`, 'info');
      } else {
        // 浮上不可 → 破壊
        destroyedCards.push(deepSeaCard.card);
        addLog(`大干ばつ: ${deepSeaCard.card.name}は浮上コストを払えず破壊された！`, 'damage');
      }
    });

    // SP消費
    if (currentSP !== opponentActiveSP) {
      setOpponentActiveSP(currentSP);
    }

    // 浮上したモンスターをフィールドに配置
    if (surfacedMonsters.length > 0) {
      setOpponentField(prev => {
        const newField = [...prev];
        let monsterIndex = 0;
        for (let i = 0; i < newField.length && monsterIndex < surfacedMonsters.length; i++) {
          if (newField[i] === null) {
            newField[i] = surfacedMonsters[monsterIndex];
            monsterIndex++;
          }
        }
        return newField;
      });
    }

    // 破壊されたカードを墓地に送る
    if (destroyedCards.length > 0) {
      setOpponentGraveyard(prev => [...prev, ...destroyedCards]);
    }

    // 深海領域をクリア
    setOpponentDeepSeaZone(prev => ({
      ...prev,
      cards: [],
    }));

    addLog(`大干ばつ: 相手の深海領域を全て干上がらせた！（浮上${surfacedMonsters.length}体、破壊${destroyedCards.length}枚）`, 'info');

    return true;
  },

  /**
   * C0000537: 干上がる海
   * 相手の深海領域のカードを1枚破壊する。
   */
  C0000537: (skillText, context) => {
    const { addLog, setPendingDeepSeaSelection } = context;
    const { currentPlayer, setOpponentGraveyard } = getPlayerContext(context);

    // 相手の深海領域を取得
    const opponentDeepSeaZone = (context as any).opponentDeepSeaZone as import('../deepSeaZone/types').DeepSeaZone | undefined;
    const setOpponentDeepSeaZone = (context as any).setOpponentDeepSeaZone as ((updater: (prev: import('../deepSeaZone/types').DeepSeaZone) => import('../deepSeaZone/types').DeepSeaZone) => void) | undefined;

    if (!opponentDeepSeaZone || !setOpponentDeepSeaZone) {
      addLog('干上がる海: 相手の深海領域が利用できません', 'info');
      return false;
    }

    // 深海領域が開放されていない、またはカードがない場合は効果なし
    if (!opponentDeepSeaZone.isUnlocked || opponentDeepSeaZone.cards.length === 0) {
      addLog('干上がる海: 相手の深海領域にカードがありません', 'info');
      return false;
    }

    const destroyCard = (deepSeaIndex: number) => {
      const target = opponentDeepSeaZone.cards[deepSeaIndex];
      if (!target) return;

      // 深海領域から削除
      setOpponentDeepSeaZone(prev => ({
        ...prev,
        cards: prev.cards.filter((_, idx) => idx !== deepSeaIndex),
      }));

      // 墓地に送る
      setOpponentGraveyard(prev => [...prev, target.card]);

      addLog(`干上がる海: ${target.card.name}を破壊！`, 'damage');
    };

    // 1枚のみの場合は自動選択
    if (opponentDeepSeaZone.cards.length === 1) {
      destroyCard(0);
      return true;
    }

    // 複数ある場合は選択UI
    if (setPendingDeepSeaSelection) {
      setPendingDeepSeaSelection({
        message: '破壊する相手の深海領域のカードを選択',
        cards: opponentDeepSeaZone.cards,
        count: 1,
        isOpponent: true,
        callback: (selectedIndices: number[]) => {
          if (selectedIndices && selectedIndices.length > 0) {
            destroyCard(selectedIndices[0]);
          }
        },
        selectionOwner: currentPlayer,
      });
      return true;
    }

    // フォールバック
    destroyCard(0);
    return true;
  },

  /**
   * C0000539: アビスイータードラゴン
   * 基本技: 相手モンスター1体に自身の攻撃力半分のダメージを与える。
   */
  C0000539: (skillText, context) => {
    const { addLog, monsterIndex, setPendingMonsterTarget } = context;
    const { myField, opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    // 基本技のみ処理
    if (!skillText.includes('基本技')) {
      return false;
    }

    // 自身の攻撃力を取得
    const self = myField[monsterIndex ?? 0];
    if (!self) {
      addLog('アビスイータードラゴン: 自身が場にいません', 'info');
      return false;
    }
    const selfAttack = self.currentAttack ?? self.attack ?? 0;
    const damage = Math.floor(selfAttack / 2);

    // 相手モンスターがいるかチェック
    const hasOpponentMonster = opponentField.some((m: MonsterInstance | null) => m !== null);
    if (!hasOpponentMonster) {
      addLog('アビスイータードラゴンの基本技: 相手モンスターがいません', 'info');
      return false;
    }

    const applyDamage = (targetIndex: number) => {
      const target = opponentField[targetIndex];
      if (!target) return;

      const newHp = (target.currentHp ?? target.hp ?? 0) - damage;
      addLog(`アビスイータードラゴンの基本技: ${target.name}に${damage}ダメージ！`, 'damage');

      if (newHp <= 0) {
        addLog(`${target.name}が破壊された！`, 'damage');
        setOpponentGraveyard((prev: CardWithId[]) => [...prev, target]);
        setOpponentField((prev: (MonsterInstance | null)[]) => {
          const newField = [...prev];
          newField[targetIndex] = null;
          return newField;
        });
      } else {
        setOpponentField((prev: (MonsterInstance | null)[]) => {
          const newField = [...prev];
          newField[targetIndex] = { ...target, currentHp: newHp };
          return newField;
        });
      }
    };

    // 相手モンスターが1体のみの場合は自動選択
    const opponentMonsters = opponentField
      .map((m: MonsterInstance | null, i: number) => ({ m, i }))
      .filter(({ m }: { m: MonsterInstance | null }) => m !== null);

    if (opponentMonsters.length === 1) {
      applyDamage(opponentMonsters[0].i);
      return true;
    }

    // 複数の場合はターゲット選択
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: `アビスイータードラゴンの基本技: ${damage}ダメージを与えるモンスターを選択`,
        targetPlayer: 'opponent',
        callback: (selectedIndex: number) => {
          applyDamage(selectedIndex);
        },
      });
      return true;
    }

    // フォールバック
    applyDamage(opponentMonsters[0].i);
    return true;
  },

  /**
   * C0000540: 捕食の大顎
   * 【刹那詠唱】相手モンスターに800ダメージ。水属性モンスターの場合、破壊する。
   */
  C0000540: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    // 相手モンスターがいるかチェック
    const hasOpponentMonster = opponentField.some((m: MonsterInstance | null) => m !== null);
    if (!hasOpponentMonster) {
      addLog('捕食の大顎: 相手モンスターがいません', 'info');
      return false;
    }

    const applyEffect = (targetIndex: number) => {
      const target = opponentField[targetIndex];
      if (!target) return;

      // 水属性なら破壊
      if (target.attribute === '水') {
        addLog(`捕食の大顎: ${target.name}は水属性のため破壊！`, 'damage');
        setOpponentGraveyard((prev: CardWithId[]) => [...prev, target]);
        setOpponentField((prev: (MonsterInstance | null)[]) => {
          const newField = [...prev];
          newField[targetIndex] = null;
          return newField;
        });
      } else {
        // 水属性でなければ800ダメージ
        const newHp = (target.currentHp ?? target.hp ?? 0) - 800;
        addLog(`捕食の大顎: ${target.name}に800ダメージ！`, 'damage');

        if (newHp <= 0) {
          addLog(`${target.name}が破壊された！`, 'damage');
          setOpponentGraveyard((prev: CardWithId[]) => [...prev, target]);
          setOpponentField((prev: (MonsterInstance | null)[]) => {
            const newField = [...prev];
            newField[targetIndex] = null;
            return newField;
          });
        } else {
          setOpponentField((prev: (MonsterInstance | null)[]) => {
            const newField = [...prev];
            newField[targetIndex] = { ...target, currentHp: newHp };
            return newField;
          });
        }
      }
    };

    // 相手モンスターが1体のみの場合は自動選択
    const opponentMonsters = opponentField
      .map((m: MonsterInstance | null, i: number) => ({ m, i }))
      .filter(({ m }: { m: MonsterInstance | null }) => m !== null);

    if (opponentMonsters.length === 1) {
      applyEffect(opponentMonsters[0].i);
      return true;
    }

    // 複数の場合はターゲット選択
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '捕食の大顎: ダメージを与えるモンスターを選択',
        targetPlayer: 'opponent',
        callback: (selectedIndex: number) => {
          applyEffect(selectedIndex);
        },
      });
      return true;
    }

    // フォールバック
    applyEffect(opponentMonsters[0].i);
    return true;
  },
};
